import { Fragment, useRef, useState } from 'react'
import { useParams } from 'react-router'

import type { Recipe } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { RECIPES } from '@ctcells/fixtures'
import { intlLocale, recipeStatusLabel, t } from '@ctcells/i18n'

import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { Button } from '../../atoms/Button.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { recipeBadgeFlat, recipeStatusTone } from '../../atoms/statusTone.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { Tabs, type TabItem } from '../../layout/Tabs.tsx'
import { gate, type ScreenAccess } from '../access.ts'
import { dateTimeText } from '../format.ts'
import { Card, DetailPage, OfflineNotice } from '../resourceParts.tsx'
import { ownerValue, type BatchOwners } from '../batches/batchOwnerColumn.tsx'
import {
  moduleStepChips,
  recipeFormFrom,
  recipeSummary,
  stepCountsOf,
  type RecipeFormState,
} from './recipeForm.ts'
import { isNewFormatRecipe, newFormatFacts } from './recipeStandardModel.ts'
import {
  RecipeCommonCard,
  RecipeModuleCards,
  RecipeReagentCard,
  RecipeTrayPathCard,
} from './RecipeSections.tsx'
import {
  RecipeLabwareTable,
  RecipeLiquidClassTable,
  RecipeParameterTable,
  RecipeStepTables,
  StepCountChips,
} from './RecipeTables.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 레시피 상세 — 와이어프레임 `recipe()` 상세 2131행.
 *
 * 🔴 **상태는 읽기 전용 배지다.** 2026-09-20 이전에는 드롭다운(작성 중·확정·보관)이었는데
 *    **서버에 그런 전이가 없다**: `draft` 로 만드는 경로가 없고(`POST /recipes` 가 곧 확정),
 *    상태를 움직이는 연산은 `POST /recipes/{id}/archive` **하나뿐**이다. 드롭다운은 고를 수
 *    없는 값 둘을 고르게 했다. → 배지 + 「보관」 버튼 하나로 바꿨다.
 * 🔴 **보관은 사유를 «사람이» 적는다.** `IrreversibleCommandEnvelope.reason` 이 필수이고
 *    그 문장이 **감사 원장에 그대로 봉인된다**(서버가 합성하지 않는다). 프론트가 채우면
 *    원장이 거짓이 된다 — 확인 대화를 한 번 거친다(사용자 비활성과 같은 모양).
 * 🔴 **보관은 제어락 대상이 아니다** — 계약이 이 연산에 `ControlToken` 을 선언하지 않았다
 *    (실서버 스펙 설명 「제어락 대상이 아니다」). `PUT /recipes/{id}` 와 묶지 마라.
 *
 * 🔴 **「검증」·「드라이런」이 없다**(eric 2026-09-29 「검증, 드라이런 이거 빼」). 머리의 버튼 둘과
 *    오른쪽 결과 카드 둘을 걷었다. 드라이런의 「예상 소요」를 다른 자리로 옮겨 살리지 마라 —
 *    상시 작업 줄에서도 예상 시간을 뺐다(eric 2026-09-28). 요청 훅(`useValidateRecipe` ·
 *    `useSimulateRecipe`)은 `packages/api` 에 남아 있고 **쓰는 화면이 없다.**
 *
 * 🔴 **본문이 탭으로 갈렸다**(2026-09-21 · Opentrons 정렬). 근거 전문은
 *    `docs/opentrons-alignment.md` §4-2 — App 의 프로토콜 상세가
 *    `파라미터 | 하드웨어 | 랩웨어 | 액체 | 통계` 탭이다.
 *    종전에는 카드 여덟 장이 **한 장에 세로로 쌓여** 있어서 스크롤 끝의 「공통」 카드를
 *    보려면 71줄짜리 시약 표를 지나야 했다.
 *
 *    | Opentrons | 우리     | 왜 그렇게 옮겼나                                           |
 *    | --------- | -------- | ---------------------------------------------------------- |
 *    | —         | 개요     | 이름 · 공통 제약(다른 탭에 속하지 않는 값)                 |
 *    | 파라미터  | 작업 생성 입력 | `runtime_parameters` — 🔴 탭 이름은 「파라미터」가 «아니다» |
 *    | 하드웨어  | —        | 로봇·피펫 구성이 응답에 «없다» ⇒ 탭을 만들지 않는다        |
 *    | 랩웨어    | 랩웨어   | `payload.labware[]` — **이번에 처음 보인다**               |
 *    | 액체      | 시약     | 시약 표 + `liquid_classes`(**이번에 처음 보인다**)         |
 *    | 통계      | —        | 집계 경로가 없다 ⇒ 탭을 만들지 않는다                      |
 *    | —         | 단계     | 모듈 파라미터 + 스텝 표 + 트레이 경로                      |
 *
 * 🔴 **「요약」의 자리는 서랍이냐에 따라 갈린다**(2026-09-29 · 랩웨어·시료 상세와 같은 규칙).
 *    | 서랍(통합 콘솔 · 목록 오른쪽 판) | 한 열. 「요약」은 **개요 탭 안**에 선다 |
 *    | 전체 페이지(장비 관점 콘솔)      | 2열(`.detail2`). 「요약」은 오른쪽 기둥에 탭 밖으로 선다 |
 *    서랍에서 요약 한 장을 세우자고 300px 기둥을 잡아 두면 옆이 텅 빈다 — 시료 상세가 「운전」 카드를
 *    서랍에서 뺀 것과 같은 까닭이다. 요약이 탭 밖에 있던 이유(검증 결과를 탭 이동에 잃지 않게)는
 *    검증이 빠지면서 사라졌다.
 */
export type RecipeDetailTab = 'overview' | 'parameters' | 'labware' | 'reagents' | 'steps'

export interface RecipeDetailScreenProps {
  recipes?: readonly Recipe[]
  access?: ScreenAccess

  /**
   * 「이 레시피로 작업 만들기」. **이동일 뿐이다 — 서버로 나가는 요청이 없다.**
   *
   * 🔴 2026-09-21 까지 이 버튼은 **`onClick` 이 없어 죽어 있었다**(확정 레시피에서 눌러도
   *    아무 일이 안 났다). 이으면서 「이 요청이 서버에서 무엇을 덮는가」를 먼저 봤고,
   *    **덮는 것이 없다**는 것이 답이다: 새 작업 화면으로 가면서 레시피를 미리 골라 둘 뿐이고
   *    `POST /batches` 는 그 화면의 「작업 만들기」가 따로 누를 때 나간다.
   * 🔴 그래서 **제어락을 여기서 요구하지 않는다.** 락은 다음 화면이 요구한다 —
   *    여기서 요구하면 「보기만 하려던 사람」이 락을 잡아야 이동할 수 있게 된다.
   * ⚠ 화면은 어디로 가는지 모른다. 이동은 컨테이너가 한다(이 리포의 화면 ↔ 라우터 경계).
   */
  onMakeBatch?: (recipeId: string) => void
  /** 🔴 확인 대화를 거친 것만 온다. `reason` 은 빈 문자열로 오지 않는다 */
  onArchive?: (recipeId: string, reason: string) => void
  /** 요청 중 — 확인 버튼 중복 클릭을 막는다(멱등키가 매번 새로 난다) */
  archiving?: boolean
  archiveError?: string | undefined
  /** 서랍으로 열렸을 때의 닫기(×). 없으면 「← 레시피」 줄이다 */
  onClose?: (() => void) | undefined
  /**
   * 「작업자」 이름표 — 🔴 **admin 에게만 값이 있다**(목록의 「작업자」 열과 같은 규칙 · `useBatchOwners` · eric 2026-09-30).
   * 넘기면 서랍 「요약」 카드에 「작업자」 줄이 이름으로 선다. 안 넘기면 줄이 없다 — 계정 ID 를 대신 적지 않는다.
   */
  owners?: BatchOwners | undefined
}

export function RecipeDetailScreen({
  recipes = RECIPES,
  access = {},
  onMakeBatch,
  onArchive,
  archiving = false,
  archiveError,
  onClose,
  owners,
}: RecipeDetailScreenProps) {
  const { recipeId } = useParams()
  const recipe = recipes.find((r) => r.recipe_id === recipeId)
  const [form, setForm] = useState<RecipeFormState | null>(null)
  const [tab, setTab] = useState<RecipeDetailTab>('overview')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reason, setReason] = useState('')
  const reasonRef = useRef<HTMLDivElement>(null)
  const v = useSubmitValidation((text: string) => ({
    reason: text.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))

  if (!recipe) {
    return (
      <DetailPage
        backLabel={t('screen.recipes.title')}
        backTo={PATHS.recipes}
        onClose={onClose}
        title={t('screen.recipeDetail.title')}
      >
        <EmptyState label={t('state.empty.recipeList')} />
      </DetailPage>
    )
  }

  /*
   * 🔴 **서랍이면 목록이 «왼쪽에 그대로 있다»**(eric 2026-09-23 「이건 row 에 보이는 거니
   *    상세에선 다 빼」). 레시피 목록의 열은 「레시피 · 상태 · 버전 · 단계 · 등록(시각 · 작성자)」이다.
   * 🔴 **전체 페이지(장비 관점 콘솔)에서는 다 보인다** — `onClose` 가 그 둘을 가르는 축이다.
   */
  const inDrawer = onClose !== undefined
  const state = form ?? recipeFormFrom(recipe)
  const counts = stepCountsOf(recipe)
  const summary = recipeSummary(state, counts)
  const isDraft = recipe.status === 'draft'
  const payload = recipe.payload
  const params = payload.runtime_parameters ?? []
  const recipeLabware = payload.labware ?? []
  const liquidClasses = payload.liquid_classes ?? {}
  /*
   * 🔴 **새 형식(Opentrons protocol/8)이면 옛 형식 읽기를 하나도 그리지 않는다**(2026-10-06 · 팀장 「거짓 표시라 고치자」).
   *    옛 요약 · 탭(매개변수 · 랩웨어 · 시약 · 단계)은 옛 형식 `payload` 의 절을 세는데 새 형식에는 그 절이 없어 전부 «0» ·
   *    빈 표가 됐다 — 「스텝 0 · 원심 0」이 «빈 레시피»로 읽혔다(dev2 `4b343fb3…` 실측). 새 형식이면 문서에 있는 값만 요약한다.
   *    판정은 서버와 같은 규칙 하나(`isNewFormatRecipe` — `$otSharedSchema` · `designerApplication`).
   */
  const newFormat = isNewFormatRecipe(payload)
  const facts = newFormat ? newFormatFacts(payload) : {}

  /**
   * 탭 옆 수는 **그 탭이 실제로 그릴 줄 수**다. 🔴 지어낸 집계가 아니다 —
   * 전부 `payload` 를 그 자리에서 센 것이고, 0 이면 `Tabs` 가 수를 안 그린다.
   * ⚠ 「탭을 만들지 않는다」와 「수가 0 이다」는 다르다. 응답에 그 절이 있으면 탭은 선다.
   */
  const tabs: TabItem<RecipeDetailTab>[] = newFormat
    ? /* 새 형식 — 개요 하나(옛 형식 탭은 그 절을 읽어 «0» · 빈 표가 된다). 탭이 하나면 탭 줄이 사라진다 */
      [{ id: 'overview', label: t('tab.recipeDetail.overview') }]
    : [
        { id: 'overview', label: t('tab.recipeDetail.overview') },
        { id: 'parameters', label: t('tab.recipeDetail.parameters'), count: params.length },
        { id: 'labware', label: t('tab.recipeDetail.labware'), count: recipeLabware.length },
        { id: 'reagents', label: t('tab.recipeDetail.reagents'), count: state.reagents.length },
        {
          id: 'steps',
          label: t('tab.recipeDetail.steps'),
          count: counts.centrifuge + counts.assembler + counts.staining,
        },
      ]

  // 값 편집은 작성 중 + control + 제어락(`PUT /recipes/{id}`). 확정·보관은 불변이다
  const editGate = gate(access, {
    perm: 'control',
    needsLock: true,
    stateOk: isDraft,
    stateReason: 'reason.draftOnly',
  })
  /**
   * 🔴 **제어락을 요구하지 않는다**(계약 — 이 연산은 `ControlToken` 비대상).
   * ⚠ `committed` 에서만 선다. `draft` 는 409 ⓐ, 이미 `archived` 는 멱등 200 이라 누를 일이 없다.
   */
  const archiveGate = archiving
    ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
    : onArchive === undefined
      ? ({ disabled: true, disabledReason: t('reason.demoSource') } as const)
      : gate(access, {
          perm: 'control',
          stateOk: recipe.status === 'committed',
          /*
           * 🔴 **`draft` 와 `archived` 를 같은 말로 막지 않는다**(2026-09-21). 종전에는 둘 다
           *    「확정된 레시피만」이었는데, **보관된 레시피에서는 틀린 말**이다 — 확정을 거친
           *    레시피이고 못 하는 까닭은 「이미 했다」다. 할 일이 정반대인데 문구가 같았다.
           */
          stateReason:
            recipe.status === 'archived' ? 'reason.recipeAlreadyArchived' : 'reason.committedOnly',
        })

  /**
   * 🔴 **제어락을 요구하지 않는다** — 이동일 뿐이다(프롭 머리말 참조).
   * ⚠ `committed` 에서만 선다. 서버 `committed_version()` 이 그 밖을 422 로 떨어뜨리므로
   *   화면이 먼저 말해 준다 — 골라 놓고 다음 화면에서 거절당하는 것보다 낫다.
   */
  const makeBatchGate =
    onMakeBatch === undefined
      ? ({ disabled: true, disabledReason: t('reason.noRouteHere') } as const)
      : gate(access, {
          perm: 'control',
          stateOk: recipe.status === 'committed',
          stateReason:
            recipe.status === 'archived' ? 'reason.archivedNoBatch' : 'reason.draftNoBatch',
        })

  /**
   * 🔴 **비활성 사유를 «화면에» 적는다.** `disabledReason` 은 툴팁(`title`) + 보조기술
   *    전용이라 눈으로는 읽히지 않는다 — eric 이 보관 레시피에서 흐린 버튼 둘을 보고
   *    *「작업 활성화가 버튼이 보여지지가 않아」* 라고 한 자리가 그것이다(2026-09-21).
   *    `OfflineNotice` 와 같은 성질이고 같은 표현(`.offline` 알약)을 쓴다.
   * ⚠ 연결이 끊긴 동안에는 내지 않는다 — 그때 사유는 전부 「연결 끊김」이고
   *   `OfflineNotice` 가 바로 위에서 이미 그 말을 한다. 같은 말을 세 번 하지 않는다.
   */
  const blocked =
    access.controlDisabled === true
      ? []
      : (
          [
            { key: 'archive', label: t('action.archive'), gate: archiveGate },
            { key: 'makeBatch', label: t('action.makeBatchFrom'), gate: makeBatchGate },
          ] as const
        ).filter((entry) => entry.gate.disabled)

  /* 옛 형식 요약 — 절 · 스텝(모듈별) · 시약 · 작업 생성 입력. 🔴 옛 형식일 때만 */
  const oldFormatRows = [
    {
      key: 'sections',
      label: t('row.sections'),
      value: <Pieces parts={summary.sectionParts} />,
    },
    {
      key: 'steps',
      label: t('row.steps'),
      /* 합계 + 모듈별 칩(디자인팀 시안 `.steps3` · 목록 「단계」 열과 같은 모양 · 2026-10-12) */
      value: (
        <span className="stepchips__row">
          <b className="stepchips__total">{summary.stepTotal.toLocaleString(intlLocale())}</b>{' '}
          <StepCountChips chips={moduleStepChips(summary.stepCounts)} />
        </span>
      ),
    },
    { key: 'reagents', label: t('row.reagents'), value: summary.reagents },
    {
      key: 'inputs',
      label: t('row.batchInputs'),
      value: <Pieces parts={summary.inputParts} />,
    },
  ]
  /* 새 형식 요약 — 문서에 있는 값만(없는 칸은 줄째 뺀다) */
  const newFormatRows = [
    ...(facts.schemaVersion === undefined
      ? []
      : [
          {
            key: 'format',
            label: t('recipeV2.format'),
            value: t('recipeV2.formatValue', { version: facts.schemaVersion }),
          },
        ]),
    ...(facts.protocolName === undefined
      ? []
      : [{ key: 'protocol', label: t('recipeV2.protocolName'), value: facts.protocolName }]),
    ...(facts.commands === undefined
      ? []
      : [
          {
            key: 'commands',
            label: t('recipeV2.commands'),
            value: facts.commands.toLocaleString(intlLocale()),
          },
        ]),
    ...(facts.labwareDefinitions === undefined
      ? []
      : [
          {
            key: 'labwareDefinitions',
            label: t('recipeV2.labwareDefinitions'),
            value: facts.labwareDefinitions.toLocaleString(intlLocale()),
          },
        ]),
    ...(facts.liquids === undefined
      ? []
      : [
          {
            key: 'liquids',
            label: t('recipeV2.liquids'),
            value: facts.liquids.toLocaleString(intlLocale()),
          },
        ]),
  ]

  const summaryCard = (
    <Card title={t('card.summary')}>
      {/*
        🔴 **서랍에서는 「버전」 한 줄이 빠진다**(eric 2026-09-23). `v2 · 확정` 은 이 상세의 부제 줄과
           상태 배지에 이미 있다 — 목록에는 버전 열이 없다(2026-09-30 eric) · 서랍에서 버전은 부제가 말한다.
        🔴 「단계」(`329 · 원심 57 · 어셈블 101 · 염색 71`)는 2026-09-28 에 서랍에 되돌렸다 — 서랍이 열리면
           목록의 단계 열이 빠지는데 모듈별 수는 이 줄 말고 볼 데가 없다.
        🔴 **여러 조각인 값은 «조각 사이»에서만 꺾인다**(`Pieces` · 2026-09-29). 종전에는 한 문자열이라
           「어셈블 / 101」처럼 조각 한가운데서 꺾였다.
        ⚠ 전체 페이지에서는 여섯 줄이 그대로 선다(서랍은 「등록」이 하나 더 붙는다).
      */}
      <ValueRows
        rows={[
          ...(inDrawer
            ? []
            : [
                {
                  key: 'version',
                  label: t('column.version'),
                  value: `v${String(recipe.version)} · ${recipeStatusLabel(recipe.status)}`,
                },
              ]),
          { key: 'schema', label: t('row.schemaVersion'), value: recipe.schema_version },
          ...(newFormat ? newFormatRows : oldFormatRows),
          /*
           * 🔴 「작업자」 · 「등록」 — **서랍일 때만** 선다(2026-09-28). 목록의 두 열이 서랍이 열리면 빠질 수 있어
           *    (`listColumnPriority.ts`) 값이 갈 곳이 여기다.
           * 🔴 **작업자는 admin 에게만 · 이름으로**(eric 2026-09-30 「작업자랑 등록 이렇게 두 열은 붙여줘」). 목록 열과 같은
           *    규칙이다 — 이름표(`owners`)를 받았을 때만 줄이 서고, 종전처럼 계정 ID 를 모두에게 적지 않는다.
           */
          ...(inDrawer && owners !== undefined
            ? [
                {
                  key: 'owner',
                  label: t('column.owner'),
                  value: ownerValue(owners, recipe.created_by ?? null),
                },
              ]
            : []),
          ...(inDrawer
            ? [
                {
                  key: 'created',
                  label: t('column.registered'),
                  value: dateTimeText(recipe.created_at),
                },
              ]
            : []),
        ]}
      />
    </Card>
  )

  const body = (
    <div className="fstack">
      {/*
        🔴 **`draft` 가 아니면 읽기 전용이다 — 비활성이 아니다.** 확정·보관 레시피는
           고칠 수 있는 종류가 아니라서 사유를 달 것이 없다(`RecipeSections.readOnly` 참조).
      */}
      {tab === 'overview' ? (
        <>
          {/*
            🔴 **「설명」칸이 없다**(2026-09-21 걷음). `Recipe` 응답 17속성에 `description` 이
               없어서 종전에는 `value=""` 가 **고정으로 박혀** 있었다 — 값이 비어서 빈 것이
               아니라 **읽을 데가 없어서** 빈 것이다. 쓰기 쪽도 같다(`RecipeWriteEnvelope` 4키 ·
               `recipe.schema.json` `additionalProperties:false`). 읽지도 쓰지도 못하는 칸이다.
          */}
          <Card title={t('card.basicInfo')}>
            <div className="f2 f2--wide">
              <Field label={t('field.name')}>
                <TextInput
                  onChange={() => undefined}
                  value={recipe.name}
                  wide
                  {...(isDraft ? editGate : ({ readOnly: true } as const))}
                />
              </Field>
            </div>
          </Card>
          {newFormat ? (
            <p className="ronote" data-recipe-format="v2" role="note">
              {t('recipeV2.note')}
            </p>
          ) : (
            <RecipeCommonCard
              edit={editGate}
              onChange={setForm}
              readOnly={!isDraft}
              recipe={recipe}
              state={state}
            />
          )}
          {inDrawer ? summaryCard : null}
        </>
      ) : null}

      {tab === 'parameters' ? <RecipeParameterTable params={params} /> : null}

      {tab === 'labware' ? <RecipeLabwareTable labware={recipeLabware} /> : null}

      {tab === 'reagents' ? (
        <>
          <RecipeReagentCard edit={editGate} onChange={setForm} readOnly={!isDraft} state={state} />
          <RecipeLiquidClassTable classes={liquidClasses} />
        </>
      ) : null}

      {tab === 'steps' ? (
        <>
          <RecipeModuleCards
            edit={editGate}
            onChange={setForm}
            readOnly={!isDraft}
            recipe={recipe}
            state={state}
          />
          <RecipeStepTables payload={payload} />
          <RecipeTrayPathCard
            edit={editGate}
            onChange={setForm}
            readOnly={!isDraft}
            recipe={recipe}
            state={state}
          />
        </>
      ) : null}
    </div>
  )

  const closeConfirm = () => {
    setConfirmOpen(false)
    setReason('')
    v.reset()
  }

  return (
    <DetailPage
      onClose={onClose}
      actions={
        <>
          <Button onClick={() => setConfirmOpen(true)} size="pill" {...archiveGate}>
            {t('action.archive')}
          </Button>
          <Button onClick={() => onMakeBatch?.(recipe.recipe_id)} size="pill" {...makeBatchGate}>
            {t('action.makeBatchFrom')}
          </Button>
        </>
      }
      backLabel={t('screen.recipes.title')}
      backTo={PATHS.recipes}
      /* 🔴 읽기 전용이다. 상태를 고르는 자리가 아니다 — 움직이는 길은 「보관」 하나뿐이다 */
      badge={
        <StatusBadge
          /* 목록 칩과 같다 — 바탕 있는 칩은 테 없는 알약(eric 2026-10-30) */
          flat={recipeBadgeFlat(recipe.status, recipeStatusTone(recipe.status))}
          label={recipeStatusLabel(recipe.status)}
          status={recipe.status}
          tone={recipeStatusTone(recipe.status)}
        />
      }
      submeta={
        <span className="runchips">
          <span>{`v${String(recipe.version)} · ${recipe.schema_version}`}</span>
          {/*
            🔴 **만든 이 는 UUID 다 — 앞 8자만 «보인다»**(2026-09-22 · 방안 A).
               값은 그대로다(툴팁·보조기술에 전문). 36자가 부제 줄의 절반을 먹어
               버전·스키마보다 사람 ID 가 먼저 읽혔다.
          */}
          {/*
            🔴 **서랍에서는 만든 이를 적지 않는다**(eric 2026-09-23). 목록의 「등록」 열이
               `등록시각 · 작성자` 로 **이미 같은 값을 보인다** — 부제 줄은 「어느 것을 열었나」를
               말하는 자리이고 그 일은 제목 · 버전 · 스키마가 한다.
            ⚠ 전체 페이지에서는 남긴다. 거기엔 목록이 없어 작성자를 볼 데가 여기뿐이다.
          */}
          {inDrawer ? null : recipe.created_by == null || recipe.created_by === '' ? (
            <span>{t('mask.none')}</span>
          ) : (
            <ShortId className="muted" value={recipe.created_by} />
          )}
        </span>
      }
      title={recipe.name}
    >
      <OfflineNotice shown={access.offline === true} />
      {/*
        🔴 **화면이 한 번 말한다 — 「이 값들은 고칠 수 없다」.** 안 말하면 칸의 생김새만 남고,
           그 생김새(테두리 있는 입력 상자)는 「고칠 수 있다」고 말한다.
        ⚠ `draft` 에서는 내지 않는다 — 그때는 진짜 고치는 화면이다.
      */}
      {/* 🔴 `.offline`(연결 끊김 · 점선)이 아니라 `.ronote` 다 — 점선은 「아직 없음」 한 뜻이다 */}
      {isDraft ? null : (
        <p className="ronote" role="status">
          {t('notice.recipeReadOnly')}
        </p>
      )}
      {blocked.length === 0 ? null : (
        <div className="runchips">
          {blocked.map((entry) => (
            <p className="offline" key={entry.key} role="status">
              {`${entry.label} — ${entry.gate.disabledReason ?? ''}`}
            </p>
          ))}
        </div>
      )}
      {/*
        🔴 **서랍에서는 하위 탭(알약)이 두 줄로 접힌다**(eric 2026-09-30 「레시피도.. 1/3만 열리게」). 밑줄 탭 다섯이 1/3 서랍을
           넘쳤다(1280 실측 +75px). 서랍은 「준비」 탭 줄 아래에 서는 구역이라 한 단계 낮은 하위 탭(`sub`)이 위계에도 맞고,
           알약은 줄이 바뀌어도 선택 표시가 흐트러지지 않는다(밑줄은 첫 줄 밑줄이 둘째 줄 위에 떠 버린다).
        ⚠ 전체 페이지(장비 관점 콘솔)는 종전 밑줄 탭 그대로다.
      */}
      {inDrawer ? (
        <div className="tabwrap">
          <Tabs
            items={tabs}
            label={t('screen.recipeDetail.title')}
            onChange={setTab}
            value={tab}
            variant="sub"
          />
        </div>
      ) : (
        <Tabs items={tabs} label={t('screen.recipeDetail.title')} onChange={setTab} value={tab} />
      )}

      {/*
        🔴 **서랍이면 한 열이다 — 오른쪽 기둥이 없다**(머리말 참조). 「요약」은 개요 탭 안에 선다.
           전체 페이지(장비 관점 콘솔)는 2열 그대로이고 「요약」이 탭 밖 오른쪽 기둥에 선다.
      */}
      {inDrawer ? (
        body
      ) : (
        <div className="detail2">
          {body}
          <div className="fstack fstack--sticky">{summaryCard}</div>
        </div>
      )}

      {/*
        🔴 **성공을 화면이 판정하지 않는다.** 보관이 통하면 무효화 → 재조회로 `status` 가
           `archived` 가 되고 **그때 대화가 사라진다.** 「보냈으니 닫는다」로 하면 서버가
           거부한 경우에도 닫혀 사람이 실패를 못 본다(낙관적 갱신과 같은 잘못이다).
        ⚠ 실패하면 `status` 가 그대로 `committed` 라 대화가 남고 사유 입력도 남는다.
      */}
      {confirmOpen && recipe.status === 'committed' ? (
        <Modal
          size="sm"
          footer={
            <>
              <Button onClick={closeConfirm} size="pill">
                {t('action.cancel')}
              </Button>
              <Button
                size="pill"
                variant="danger"
                {...archiveGate}
                onClick={() => {
                  if (!archiveGate.disabled && v.validate(reason, reasonRef.current))
                    onArchive?.(recipe.recipe_id, reason.trim())
                }}
              >
                {t('action.archive')}
              </Button>
            </>
          }
          onClose={closeConfirm}
          title={t('screen.recipeArchive.title')}
        >
          {/* 🔴 설명 문장을 달지 않는다(§1-1). 대상과 성질을 값으로만 보인다 */}
          <ValueRows
            rows={[
              { key: 'name', label: t('field.name'), value: recipe.name },
              {
                key: 'version',
                label: t('column.version'),
                value: `v${String(recipe.version)}`,
              },
              // 계약 문면 「단방향 — 복원 = 새 버전 재커밋」
              { key: 'reversible', label: t('row.reversible'), value: t('value.irreversible') },
            ]}
          />
          {/* 🔴 계약 필수값. 적은 문장이 **그대로** 감사행에 실린다 — 합성하지 않는다 */}
          <div ref={reasonRef}>
            <Field {...v.field('reason').fieldProps} label={t('field.reason')}>
              <TextInput
                {...v.field('reason').inputProps}
                onChange={(next) => {
                  setReason(next)
                  v.change('reason', next)
                }}
                value={reason}
                wide
              />
            </Field>
          </div>
          {/* 🔴 실패해도 대화가 닫히지 않는다 — 사유를 다시 적게 하지 않으려는 것이다 */}
          {archiveError === undefined ? null : (
            <p className="vissue" role="alert">
              {archiveError}
            </p>
          )}
        </Modal>
      ) : null}
    </DetailPage>
  )
}

/**
 * ` · ` 로 잇는 값 — **조각 사이에서만 꺾인다.** 조각 안(「어셈블 101」 · 「1차 항체 용액 lot」)은 붙어 있다.
 *
 * - 구분점은 **앞 조각 끝에** 붙는다 — 꺾인 줄이 「·」로 시작하지 않는다
 * - ⚠ 오른쪽 정렬은 `.a-rows__v` 가 쥔다. 여기서 flex 로 감싸지 마라 — 종전 「등록」 줄이 `.btnrow`(flex)로
 *   감싸여 **그 줄만 왼쪽으로 붙었다**
 * - 글자는 그대로 이어진다(`원심 57 · 어셈블 101`) — 보조기술·복사에서 모양이 같다
 */
function Pieces({ parts }: { parts: readonly string[] }) {
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={`${String(index)}-${part}`}>
          {index === 0 ? null : ' '}
          <span className="nowrap">{index < parts.length - 1 ? `${part} ·` : part}</span>
        </Fragment>
      ))}
    </>
  )
}
